<template>
  <div v-if="visible" class="report-summary-metrics">
    <div class="summary-metrics">
      <!-- Owner controls only. There is deliberately no heading: on a report
           that carries just a note, a "Summary" label sat above a paragraph
           that was not summarising anything, and the cards below label
           themselves. -->
      <div v-if="editable && (isEnabled || canChooseCharts)" class="summary-metrics__actions">
        <!-- There is no Save button: a checkbox writes the moment you tick it.
             That is only obvious if the panel says so, and a save that fails
             has to say that too — the first cut did neither, so a picker whose
             writes were going nowhere looked exactly like one that worked. -->
        <span
          v-if="saveStatus"
          class="summary-metrics__status"
          :class="{ 'summary-metrics__status--error': saveState === 'error' }"
          role="status"
        >
          {{ saveStatus }}
        </span>
        <button
          class="ui basic mini button"
          :class="{ active: isConfiguring }"
          @click="isConfiguring = !isConfiguring"
        >
          <i class="icon sliders horizontal"></i>
          {{ isEnabled ? "Choose metrics" : "Choose charts" }}
        </button>
        <button
          v-if="isEnabled"
          class="ui basic mini icon button"
          title="Remove metrics"
          @click="removeSection"
        >
          <i class="icon trash alternate outline"></i>
        </button>
      </div>

      <!-- The report's notes. They used to sit in the header, a separate prose
           box a long way above these numbers; one block reads as one summary.
           Storage is unchanged (report.sections.description), so the 5,714
           reports that already have notes keep rendering them. -->
      <text-editor
        v-if="editable"
        v-model="report.sections.description"
        :class="{ empty: !report.sections.description }"
        :initial-config="textEditorConfig"
        placeholder="Add a note for whoever reads this report (optional)"
        class="clearing floating-menu summary-metrics__notes-editor"
      />
      <div
        v-else-if="report.sections && report.sections.description"
        id="reportDescription"
        class="summary-metrics__notes"
        v-html="autolinkedNotes"
      />

      <div v-if="isConfiguring && editable" class="summary-metrics__config">
        <div
          v-for="group in isEnabled ? groups : []"
          :key="group.key"
          class="summary-metrics__group"
        >
          <div class="summary-metrics__group-head">
            <span class="summary-metrics__group-label">{{ group.label }}</span>
            <span v-if="group.hint" class="summary-metrics__group-hint">{{
              group.hint
            }}</span>
          </div>
          <div class="summary-metrics__options">
            <div
              v-for="metric in group.metrics"
              :key="metric.key"
              class="summary-metrics__option"
              :class="{
                'summary-metrics__option--unavailable': !metric.available,
              }"
            >
              <div class="ui checkbox">
                <input
                  :id="`summary-metric-${metric.key}`"
                  type="checkbox"
                  :checked="selectedKeys.includes(metric.key)"
                  @change="toggleMetric(metric.key)"
                />
                <label :for="`summary-metric-${metric.key}`">{{
                  metric.label
                }}</label>
              </div>
              <span class="summary-metrics__option-hint">{{
                metric.available ? metric.hint : metric.unavailableReason
              }}</span>
            </div>
          </div>
        </div>

        <!-- The donuts under this section. Same storage pattern as the cards
             (report.sections.charts), same autosave. Paid media donuts are
             not listed: they still appear whenever there is boosted data. -->
        <div v-if="canChooseCharts" class="summary-metrics__group">
          <div class="summary-metrics__group-head">
            <span class="summary-metrics__group-label">Charts</span>
          </div>
          <div class="summary-metrics__options">
            <div
              v-for="chart in chartOptions"
              :key="chart.key"
              class="summary-metrics__option"
              :class="{
                'summary-metrics__option--unavailable': !chart.available,
              }"
            >
              <div class="ui checkbox">
                <input
                  :id="`report-chart-${chart.key}`"
                  type="checkbox"
                  :checked="chartMetrics.includes(chart.key)"
                  @change="toggleChart(chart.key)"
                />
                <label :for="`report-chart-${chart.key}`">{{
                  chart.label
                }}</label>
              </div>
              <span class="summary-metrics__option-hint">{{
                chart.available ? chart.hint : "No data in this report"
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <button
        v-if="!isEnabled && editable"
        class="ui basic button summary-metrics__add"
        @click="enableSection"
      >
        <i class="icon plus"></i>
        Add metrics
      </button>

      <div v-else-if="isEnabled && cards.length" class="summary-metrics__cards">
        <div
          v-for="card in cards"
          :key="card.key"
          class="summary-metrics__card"
        >
          <div class="summary-metrics__card-label">
            {{ card.label }}
            <span v-if="card.hint" class="summary-metrics__card-hint">{{
              card.hint
            }}</span>
          </div>
          <div class="summary-metrics__card-value">{{ card.value }}</div>
        </div>
      </div>
      <p v-else-if="isEnabled" class="summary-metrics__empty">
        No data for the selected metrics
      </p>

      <!-- Same rule the donuts below state, said once up here too: YouTube is
           reported separately, not folded in. Without this line "Posts 18" on a
           report listing 21 reads as a miscount. -->
      <p
        v-if="isEnabled && cards.length && excludedPostCount"
        class="summary-metrics__scope-note"
      >
        Excludes {{ excludedPostCount }}
        {{ excludedPostCount === 1 ? "YouTube post" : "YouTube posts" }}, which
        are reported separately below.
      </p>
    </div>
  </div>
</template>

<script>
import linkifyHtml from "linkify-html";
import textEditor from "components/textEditor.vue";
import { CHART_METRICS, isCharted } from "lib/reportChartCatalog.mjs";

const $ = window.jQuery;
import {
  DEFAULT_METRICS,
  METRIC_DEFINITIONS,
  METRIC_GROUPS,
  computeMetric,
  findMetric,
  formatCurrency,
  formatMetric,
  totalsFor,
} from "lib/summaryMetricCatalog.mjs";

export default {
  components: { textEditor },
  props: {
    deliverables: { type: Array, default: () => [] },
    report: { type: Object, required: true },
    editable: { type: Boolean, default: false },
    // Report#creator_cost_breakdown — {total, priced, unpriced}, summed over
    // the creators whose deliverables are in this report. Aggregate only: the
    // per-creator rates behind it never leave the server. Read live rather
    // than stored, so correcting a rate on the campaign fixes the report's CPM
    // on the next load with nothing to re-enter.
    campaignCost: { type: Object, default: () => ({}) },
    // The donuts this report draws (report.vue#reportChartMetrics). Null on
    // surfaces without a saved report, which have no Charts group to offer.
    chartMetrics: { type: Array, default: null },
  },
  data() {
    return {
      isConfiguring: false,
      // null | "saving" | "saved" | "error" — see the actions-row comment.
      saveState: null,
      // Carried over verbatim from header.vue so notes keep the same toolbar
      // and upload endpoints they have always had.
      textEditorConfig: {
        stylesSet: "fomantic-ui",
        autoParagraph: false,
        extraPlugins: "image2,tableresize,emoji,uploadimage,embed",
        removePlugins: "showborders",
        uploadUrl: "/uploads.json",
        embed_provider: "/embed/oembed?url={url}&callback={callback}",
        resize_maxWidth: "100",
        toolbar: [
          { name: "links", items: ["Link", "Unlink", "Image", "Embed"] },
          {
            name: "basicstyles",
            items: ["Bold", "Italic", "Underline", "Styles", "RemoveFormat"],
          },
          {
            name: "paragraph",
            items: [
              "NumberedList",
              "BulletedList",
              "-",
              "Outdent",
              "Indent",
              "-",
              "Blockquote",
              "-",
              "JustifyLeft",
              "JustifyCenter",
              "JustifyRight",
              "JustifyBlock",
              "-",
            ],
          },
          { name: "colors", items: ["TextColor", "BGColor"] },
          { name: "insert", items: ["Table", "HorizontalRule"] },
        ],
        filebrowserBrowseUrl: "/uploads",
        filebrowserImageBrowseLinkUrl: "/uploads",
        filebrowserImageBrowseUrl: "/uploads",
        height: 100,
      },
    };
  },
  computed: {
    saveStatus() {
      if (this.saveState === "saving") return "Saving…";
      if (this.saveState === "saved") return "Saved";
      if (this.saveState === "error") return "Couldn't save — try again";
      return null;
    },
    config() {
      return (
        (this.report.sections && this.report.sections.summaryMetrics) || null
      );
    },
    isEnabled() {
      return !!(this.config && this.config.enabled);
    },
    hasNotes() {
      return !!(this.report.sections && this.report.sections.description);
    },
    // The panel is the report's summary block: the note AND the numbers. It
    // shows for an owner (who can add either), and for a viewer whenever there
    // is something to show. The notes half is what makes the second clause
    // matter — a report that has only ever had notes, which is 5,714 of them,
    // must keep rendering exactly as it did.
    visible() {
      return this.editable || this.isEnabled || this.hasNotes;
    },
    autolinkedNotes() {
      return linkifyHtml(unescape(this.report.sections.description));
    },
    cost() {
      return Number(this.campaignCost && this.campaignCost.total) || 0;
    },
    // Provenance, then coverage. Rate coverage is thin — both rate columns are
    // nullable and neither auto-populates — so a sum over 3 of 6 creators is
    // plausible-looking and short. Say which, rather than let it silently
    // become a CPM.
    // No rate can exist on this report (a creator's own posts, with no brand
    // deal behind them), so the only cost metric left is paid media CPM, which
    // comes off the deliverables. Saying "from creator rates" there would be
    // describing a thing that does not apply.
    priceable() {
      return !!(this.campaignCost && this.campaignCost.priceable);
    },
    costGroupHint() {
      if (!this.priceable) return "From boosted post data";

      const parts = ["From creator rates"];
      if (this.unpricedCreators) {
        parts.push(
          `${this.unpricedCreators} of ${
            this.unpricedCreators + this.pricedCreators
          } creators have no rate`
        );
      }
      parts.push("visible on the share link");
      return parts.join(" · ");
    },
    unpricedCreators() {
      return Number(this.campaignCost && this.campaignCost.unpriced) || 0;
    },
    pricedCreators() {
      return Number(this.campaignCost && this.campaignCost.priced) || 0;
    },
    selectedKeys() {
      const keys = this.config && this.config.metrics;
      return Array.isArray(keys) && keys.length ? keys : DEFAULT_METRICS;
    },
    totals() {
      return totalsFor(this.deliverables);
    },
    // These cards cover the same posts the donuts do — YouTube is reported on its
    // own, not folded in. Worth one line of copy: without it "Posts 18" under a
    // report listing 21 is just a number that looks wrong.
    excludedPostCount() {
      return this.totals.excluded_post_count || 0;
    },
    computeOptions() {
      return { cost: this.cost };
    },
    // Only cards we can actually work out get rendered. A row of "—" tells a
    // client nothing and reads as broken; the picker explains the gap instead.
    //
    // Gated on isEnabled by the template too: selectedKeys defaults to
    // DEFAULT_METRICS for a report that has never configured the section, which
    // is what an owner wants the moment they switch it on — but would otherwise
    // put a metrics block on every report that has only ever carried a note.
    cards() {
      if (!this.isEnabled) return [];
      return this.selectedKeys
        .map((key) => {
          const def = findMetric(key);
          if (!def) return null;
          const value = computeMetric(key, this.totals, this.computeOptions);
          if (value === null) return null;
          return {
            key,
            label: def.label,
            hint:
              def.group === "rates" || def.group === "cost" ? def.hint : null,
            value: formatMetric(key, value),
          };
        })
        .filter(Boolean);
    },
    canChooseCharts() {
      return Array.isArray(this.chartMetrics);
    },
    chartOptions() {
      return CHART_METRICS.map((chart) => ({
        ...chart,
        available: (this.totals[chart.key] || 0) > 0,
      }));
    },
    groups() {
      return METRIC_GROUPS.map((group) => ({
        ...group,
        hint: group.key === "cost" ? this.costGroupHint : group.hint,
        metrics: METRIC_DEFINITIONS.filter(
          (def) => def.group === group.key && (this.priceable || !def.needsCost)
        ).map((def) => {
          const available =
            computeMetric(def.key, this.totals, this.computeOptions) !== null;
          return {
            key: def.key,
            label: def.label,
            // A total that is also a donut on this report says so; one that
            // is not is the only place the figure appears.
            hint:
              def.group === "totals" && isCharted(def.key, this.chartMetrics)
                ? "Charted"
                : def.hint || "",
            available,
            unavailableReason:
              def.needsCost && !this.cost
                ? "No creator rates on file"
                : "No data in this report",
          };
        }),
      })).filter((group) => group.metrics.length);
    },
  },
  watch: {
    // The report arrives asynchronously (the store replaces `report` after
    // this component is created), so seed the drafts once it lands — and only
    // then, so an owner's in-flight typing is never clobbered by a re-save.
    config: {
      immediate: true,
      handler(val, old) {
        if (!val || old) return;
      },
    },
    // Fomantic's checkbox module binds on init, so it has to run after the
    // picker is in the DOM. Same pattern as checkboxField.vue / TaskItem.vue.
    isConfiguring(open) {
      if (open)
        this.$nextTick(() => $(this.$el).find(".ui.checkbox").checkbox());
    },
  },
  beforeDestroy() {
    if (this._savedTimer) clearTimeout(this._savedTimer);
  },
  methods: {
    // "Saved" is an acknowledgement, not a permanent label — it clears itself
    // so the row goes back to just the controls. "Couldn't save" stays put.
    clearSavedLater() {
      if (this._savedTimer) clearTimeout(this._savedTimer);
      this._savedTimer = setTimeout(() => {
        if (this.saveState === "saved") this.saveState = null;
      }, 2500);
    },
    enableSection() {
      this.updateConfig({
        enabled: true,
        metrics: DEFAULT_METRICS,
      });
      this.isConfiguring = true;
    },
    removeSection() {
      this.updateConfig({ enabled: false });
      this.isConfiguring = false;
    },
    toggleChart(key) {
      const next = this.chartMetrics.filter((k) => k !== key);
      if (next.length === this.chartMetrics.length) next.push(key);
      this.persistSection("charts", { metrics: next });
    },
    toggleMetric(key) {
      const next = [...this.selectedKeys];
      const index = next.indexOf(key);
      if (index > -1) next.splice(index, 1);
      else next.push(key);
      this.updateConfig({ metrics: next });
    },
    // Mirrors report.vue#persistDefault: owners persist, share-link viewers
    // never write. `editable` is also enforced server-side by
    // ReportPolicy#update?.
    updateConfig(patch) {
      this.persistSection(
        "summaryMetrics",
        Object.assign({}, this.config, patch)
      );
    },
    persistSection(key, value) {
      if (!this.editable || !this.report || !this.report.id) return;
      if (!this.report.sections) this.$set(this.report, "sections", {});
      this.$set(this.report.sections, key, value);
      this.saveState = "saving";
      this.$store
        .dispatch("report/save", {
          id: this.report.id,
          name: this.report.name,
          sections: this.report.sections,
        })
        .then(() => {
          this.saveState = "saved";
          this.clearSavedLater();
        })
        .catch(() => {
          // The panel keeps the choice on screen — re-ticking a box the server
          // rejected is worse than leaving it and saying so — but the row now
          // says the report did not take it.
          this.saveState = "error";
        });
    },
  },
};
</script>
